import { z } from 'zod';

export const Role = z.enum(['admin', 'member']);
export type Role = z.infer<typeof Role>;

export const Units = z.enum(['lb', 'kg']);
export type Units = z.infer<typeof Units>;

export const DisplayName = z.string().trim().min(1, 'Enter a name').max(50, 'Keep it under 50 characters');
export const PasskeyName = z.string().trim().min(1).max(50);
export const Timezone = z
  .string()
  .max(64)
  .refine((tz) => {
    try {
      new Intl.DateTimeFormat('en', { timeZone: tz });
      return true;
    } catch {
      return false;
    }
  }, 'Unknown time zone');

export const User = z.object({
  id: z.string(),
  displayName: z.string(),
  role: Role,
  units: Units,
  timezone: z.string(),
});
export type User = z.infer<typeof User>;

export const SessionResponse = z.object({ user: User.nullable() });
export type SessionResponse = z.infer<typeof SessionResponse>;

export const UserResponse = z.object({ user: User });

// --- Invites ---

export const InvitePurpose = z.enum(['signup', 'add_passkey']);
export type InvitePurpose = z.infer<typeof InvitePurpose>;

/** What an invite link shows before it's used. */
export const InviteInfo = z.object({
  purpose: InvitePurpose,
  /** signup: the note the admin left (often the person's name). add_passkey: the account's name. */
  name: z.string().nullable(),
  expiresAt: z.string(),
});
export type InviteInfo = z.infer<typeof InviteInfo>;

export const InviteTokenRequest = z.object({ token: z.string().min(1).max(100) });

export const InviteSummary = z.object({
  id: z.string(),
  purpose: InvitePurpose,
  role: Role.nullable(),
  note: z.string().nullable(),
  forUser: z.string().nullable(),
  createdAt: z.string(),
  expiresAt: z.string(),
});
export type InviteSummary = z.infer<typeof InviteSummary>;

export const CreatedInvite = z.object({ invite: InviteSummary, url: z.string() });
export type CreatedInvite = z.infer<typeof CreatedInvite>;

export const CreateInviteRequest = z.object({
  role: Role,
  note: z.string().trim().max(50).optional(),
});

// --- Passkey ceremonies ---
// The WebAuthn payloads themselves are validated by SimpleWebAuthn on the
// server, so they pass through here as opaque JSON.

const WebAuthnJson = z.record(z.string(), z.unknown());

export const RegisterOptionsRequest = z.object({
  /** Invite token. Omit to add a passkey to the signed-in account. */
  token: z.string().min(1).max(100).optional(),
  /** Required for signup invites. */
  displayName: DisplayName.optional(),
  timezone: Timezone.optional(),
});
export type RegisterOptionsRequest = z.infer<typeof RegisterOptionsRequest>;

export const CeremonyOptions = z.object({ ceremonyId: z.string(), options: WebAuthnJson });
export type CeremonyOptions = z.infer<typeof CeremonyOptions>;

export const CeremonyVerifyRequest = z.object({
  ceremonyId: z.string().min(1).max(100),
  response: WebAuthnJson,
});

// --- Account ---

export const Passkey = z.object({
  id: z.string(),
  name: z.string(),
  /** Synced through a password manager (iCloud Keychain, Google, …) rather than tied to one device. */
  synced: z.boolean(),
  createdAt: z.string(),
  lastUsedAt: z.string().nullable(),
});
export type Passkey = z.infer<typeof Passkey>;

export const PasskeyList = z.object({ passkeys: z.array(Passkey) });

export const UpdateMeRequest = z
  .object({ displayName: DisplayName, units: Units, timezone: Timezone })
  .partial();

export const RenamePasskeyRequest = z.object({ name: PasskeyName });

export const AdminUser = User.extend({ createdAt: z.string(), passkeyCount: z.number() });
export type AdminUser = z.infer<typeof AdminUser>;

export const AdminUserList = z.object({ users: z.array(AdminUser), signupsOpen: z.boolean() });
export const InviteList = z.object({ invites: z.array(InviteSummary) });

/** Error body for every non-2xx API response. */
export const ApiErrorBody = z.object({ error: z.string(), message: z.string() });

export const OkResponse = z.object({ ok: z.literal(true) });

/** A passkey prompt that failed in the browser, reported so it shows up in the server log. */
export const PasskeyFailureReport = z.object({
  context: z.enum(['register', 'login']),
  name: z.string().max(100),
  message: z.string().max(500),
  code: z.string().max(100).optional(),
  cause: z.string().max(300).optional(),
});
export type PasskeyFailureReport = z.infer<typeof PasskeyFailureReport>;
